/**
 * Derived operational "Stage" — single semantic state that collapses the
 * 5 raw status fields (qbImportStatus, postingStatus, matchStatus,
 * qbDriftDetected, invoiceRequired) into 1 label the operator can scan.
 *
 * Priority order matters: a row in `posting_failed` is a FAILED row
 * regardless of its match status, so we check failure first; a row
 * already in QBD with drift is DRIFT, even if it's also "matched"; etc.
 * If a future combination doesn't match any rule, we fall back to NEW
 * so the row is visible (never silently disappears).
 *
 * Stages and their canonical actions:
 *   NEW            → no action, agent will pick it up
 *   AUTO-FLOW      → no action, posts without invoice (Michelle's rule)
 *   NEEDS INVOICE  → /matches
 *   READY          → no action, agent_3 about to enqueue
 *   IN QBD         → no action (success state)
 *   DRIFT          → investigate (manual QB change happened)
 *   DUPLICATE      → /duplicates
 *   FAILED         → /pipeline-errors
 *   REVERSED       → audit, then either re-process or accept removal
 */

export type StageKey =
  | "new"
  | "skipped"
  | "auto_flow"
  | "needs_invoice"
  | "matching"
  | "ready"
  | "review"
  | "in_qbd"
  | "drift"
  | "duplicate"
  | "failed"
  | "reversed";

// GL-review reasons → single source of truth (convex/lib/reviewReasons).
import { isReviewReason, needsGlReview } from "../../../convex/lib/reviewReasons";

export interface Stage {
  key: StageKey;
  label: string;
  color: "default" | "primary" | "secondary" | "info" | "success" | "warning" | "error";
  icon: string; // emoji or single-char hint shown next to the label
  /** Route the operator should open if the row needs human action. */
  actionRoute?: string;
  /** Free-text shown on hover (tooltip). */
  tooltip: string;
}

export interface TransactionLike {
  qbImportStatus?: string;
  postingStatus?: string;
  matchStatus?: string;
  qbTransactionId?: string;
  qbDriftDetected?: boolean;
  invoiceRequired?: boolean;
  isDuplicate?: boolean;
  /** Set by recovery:markReversed after a successful TxnDel. Optional. */
  qbDriftReason?: string;
  /** GL-review reason — if set (and pending_review), the row is in /review-queue. */
  vendorGlReviewReason?: string;
  /** Assigned GL account number. Its presence means the row has been categorized
   *  (a row with a GL is never "New" — see the fallback guard). */
  glAccountNumber?: string;
  /** How the GL was assigned (exact_lookup / fuzzy_lookup / direction_default /
   *  review_manual). Present ⇒ the pipeline categorized this row. */
  categorizationSource?: string;
}

/**
 * Compute the operator-facing stage. Returns one entry from STAGE_DEFS.
 * Pure: same input → same output, no I/O.
 */
export function computeStage(t: TransactionLike): Stage {
  // 0. SKIPPED — terminal: a zero-amount / informational statement line (e.g. a
  // $0.00 "fee waiver" / rewards notation). The pipeline refuses to post $0 to
  // QBD, so these are parked here, NOT re-driven. Wins over everything.
  if (t.postingStatus === "skipped") return STAGE_DEFS.skipped;

  // 1. DUPLICATE — wins over everything; dedup blocked this row from QB write
  if (t.qbImportStatus === "duplicate") return STAGE_DEFS.duplicate;

  // 2. FAILED — any posting_failed needs operator attention
  if (t.postingStatus === "posting_failed") return STAGE_DEFS.failed;

  // 3. DRIFT — currently in QBD but our cache says it diverged
  if (
    t.qbImportStatus === "imported" &&
    t.qbDriftDetected === true
  ) {
    return STAGE_DEFS.drift;
  }

  // 4. IN QBD — happy path: posted, in sync
  if (
    t.qbImportStatus === "imported" &&
    t.postingStatus === "posted"
  ) {
    return STAGE_DEFS.in_qbd;
  }

  // 5. REVERSED — was in QBD (had a qbTransactionId) but TxnDel cleared it.
  //    Detected by: qbImportStatus back to "pending" + postingStatus "pending"
  //    + the existence of `qbDriftReason` containing "reverse"-flavoured text
  //    OR a stored historical marker. Without a dedicated field we look for
  //    the absence of qbTransactionId AND the recovery audit signature.
  //    For now: opportunistic detection via qbDriftReason hint.
  if (
    t.qbImportStatus === "pending" &&
    t.postingStatus === "pending" &&
    typeof t.qbDriftReason === "string" &&
    /reverse|txn_del/i.test(t.qbDriftReason)
  ) {
    return STAGE_DEFS.reversed;
  }

  // 5b. REVIEW QUEUE — anchored on the GL-review REASON (not postingStatus, the
  //     CAREERS-USA bug). 2026-06-18 (Michelle's rule): a flagged row ONLY
  //     belongs in the Review Queue once it has an invoice (matched / proposed)
  //     OR the account is invoice-exempt — needsGlReview() encodes that. A
  //     flagged row WITHOUT an invoice falls through to NEEDS INVOICE below, so
  //     the GL decision waits until the supporting invoice is attached.
  //     MUST come before ready/auto_flow.
  if (needsGlReview(t)) {
    return STAGE_DEFS.review;
  }

  // 5c. MATCHING — an invoice match has been PROPOSED (matchStatus="human_review")
  //     and is waiting for a human to confirm it in /matches. The invoice IS
  //     present (proposed), so this is NOT "Needs Invoice" (no invoice) and NOT
  //     "Ready" (won't auto-post until confirmed — agent_3 only posts matched).
  //     Comes after REVIEW (a GL-review reason still wins) and before READY.
  if (t.matchStatus === "human_review") {
    return STAGE_DEFS.matching;
  }

  // 6. READY — matched, agent_3 will post on next cycle. "approved" = a human
  // approved it on /review-queue and the QB write is enqueued/in-flight — it
  // belongs here, not in the "new" fallback (which made it read "New").
  if (
    t.matchStatus === "matched" &&
    (t.postingStatus === "ready_for_posting" ||
      t.postingStatus === "processing" ||
      t.postingStatus === "categorized" ||
      t.postingStatus === "reviewed" ||
      t.postingStatus === "approved")
  ) {
    return STAGE_DEFS.ready;
  }

  // 7. AUTO-FLOW — Michelle's no-invoice rule applies; will post without match.
  //
  // We INCLUDE postingStatus="pending_review" here because pre-v3.0.37 the
  // old "all-require-invoice" gate parked these rows in pending_review;
  // now that invoiceRequired=false they should re-flow on the next agent_3
  // cycle. The recovery:requeueAutoFlowReleased mutation actively moves
  // them back to ready_for_posting, but until that runs (or for rows
  // missed by it), tagging them as AUTO-FLOW gives the operator the
  // correct mental model: "this row doesn't need an invoice; it's just
  // waiting for the agent to notice."
  if (
    t.invoiceRequired === false &&
    (t.postingStatus === "categorized" ||
      t.postingStatus === "ready_for_posting" ||
      t.postingStatus === "reviewed" ||
      t.postingStatus === "processing" ||
      t.postingStatus === "approved" ||
      t.postingStatus === "pending_review" ||
      t.postingStatus === "pending")
  ) {
    return STAGE_DEFS.auto_flow;
  }

  // 8. NEEDS INVOICE — held by the Golden Rule gate, OR a review-eligible row
  //    (has a GL-review reason) that does NOT have its invoice yet. The latter
  //    is the 2026-06-18 client rule: such rows wait here, NOT in the Review
  //    Queue, until an invoice is matched. (needsGlReview already returned
  //    false for them above precisely because matchStatus is "unmatched".)
  if (
    t.invoiceRequired !== false &&
    t.matchStatus !== "matched" &&
    t.matchStatus !== "human_review" &&
    (t.postingStatus === "pending_review" ||
      t.postingStatus === "categorized" ||
      isReviewReason(t.vendorGlReviewReason))
  ) {
    return STAGE_DEFS.needs_invoice;
  }

  // 8b. CATEGORIZED-BUT-SLIPPED-THROUGH — a row that already has a GL is, by
  //     definition, past "New" (the NEW fallback is for UNcategorized rows). It
  //     can land here when a transient post-categorize error regressed
  //     postingStatus back to "pending" while the assigned GL persisted (the
  //     agent-3 catch-block bug, fixed 2026-06-23) — which previously orphaned
  //     it as "New" with no sweep class to re-drive it. Classify by what it
  //     still needs so it is visible and keeps moving:
  //       matched           → READY (its invoice is in hand; about to post)
  //       unmatched         → NEEDS INVOICE (categorized, awaiting its invoice)
  //     (invoiceRequired===false → AUTO-FLOW and human_review → MATCHING were
  //     already caught above.)
  const hasGl =
    typeof t.glAccountNumber === "string" && t.glAccountNumber.trim() !== "";
  if (hasGl) {
    return t.matchStatus === "matched"
      ? STAGE_DEFS.ready
      : STAGE_DEFS.needs_invoice;
  }

  // 9. Fallback NEW — truly uncategorized (no GL) or anything we don't recognise
  return STAGE_DEFS.new;
}

export const STAGE_DEFS: Record<StageKey, Stage> = {
  new: {
    key: "new",
    label: "New",
    color: "default",
    icon: "·",
    tooltip: "Just ingested — waiting for agent_3 to categorize.",
  },
  auto_flow: {
    key: "auto_flow",
    label: "Auto-flow",
    color: "info",
    icon: "▶",
    tooltip:
      "No invoice required for this account (9167/9169/9170 or non-FEDEX 9183) — will post to QBD without a match.",
  },
  needs_invoice: {
    key: "needs_invoice",
    label: "Needs Invoice",
    color: "warning",
    icon: "📎",
    tooltip:
      "Golden Rule held this row — needs a matched invoice before it can post to QBD.",
    actionRoute: "/matches",
  },
  matching: {
    key: "matching",
    label: "Matching",
    color: "info",
    icon: "🔗",
    tooltip:
      "An invoice match has been proposed — confirm it on the Matches screen to post it to QBD.",
    actionRoute: "/matches",
  },
  review: {
    key: "review",
    label: "Review Queue",
    color: "warning",
    icon: "📝",
    tooltip:
      "Awaiting a human GL decision on the Review Queue (e.g. unknown vendor, OCR-exception, card mismatch).",
    actionRoute: "/review-queue",
  },
  ready: {
    key: "ready",
    label: "Ready",
    color: "secondary",
    icon: "→",
    tooltip: "Matched + categorized — agent_3 will enqueue to QBD on the next cycle.",
  },
  in_qbd: {
    key: "in_qbd",
    label: "In QBD",
    color: "success",
    icon: "✓",
    tooltip: "Posted to QuickBooks Desktop, in sync.",
  },
  drift: {
    key: "drift",
    label: "Drift",
    color: "error",
    icon: "⚠",
    tooltip:
      "Posted to QBD but the drift scanner detected divergence — investigate before trusting the QB value.",
  },
  duplicate: {
    key: "duplicate",
    label: "Duplicate",
    color: "warning",
    icon: "📋",
    tooltip:
      "Dedup blocked this row from QBD — verify it's truly a duplicate vs a legit separate charge.",
    actionRoute: "/duplicates",
  },
  failed: {
    key: "failed",
    label: "Failed",
    color: "error",
    icon: "✗",
    tooltip: "Posting to QBD failed — check the error in Pipeline Errors.",
    actionRoute: "/pipeline-errors",
  },
  reversed: {
    key: "reversed",
    label: "Reversed",
    color: "default",
    icon: "↶",
    tooltip:
      "Was previously in QBD; TxnDel removed it. Audit trail records the reason.",
  },
  skipped: {
    key: "skipped",
    label: "Skipped",
    color: "default",
    icon: "∅",
    tooltip:
      "Zero-amount / informational statement line (e.g. a $0.00 fee-waiver or rewards notation) — nothing to post to QBD.",
  },
};

/** UI-friendly ordering for filter dropdowns + KPI cards. */
export const STAGE_ORDER: StageKey[] = [
  "review",
  "needs_invoice",
  "matching",
  "failed",
  "drift",
  "duplicate",
  "auto_flow",
  "ready",
  "in_qbd",
  "new",
  "reversed",
  "skipped",
];
